/**
 * offlineQueue — buffers API requests while the device is offline.
 *
 * When the browser goes offline:
 *   - Requests that fail with a network error are added to the queue.
 *   - Each entry stores the original Axios config + resolve/reject callbacks.
 *
 * When the browser comes back online:
 *   - The queue is drained: each request is retried via the api instance.
 *   - A fresh token is attached by the request interceptor automatically.
 *
 * Usage:
 *   offlineQueue.enqueue(config) → Promise  (caller awaits this)
 *   offlineQueue.drain(apiInstance)          (called by the online listener)
 */

const _queue = [];

const offlineQueue = {
  /** Add a failed request to the queue. Returns a promise the caller can await. */
  enqueue(config) {
    return new Promise((resolve, reject) => {
      _queue.push({ config, resolve, reject });
    });
  },

  /** Retry all queued requests using the provided axios instance. */
  async drain(apiInstance) {
    if (_queue.length === 0) return;

    // Snapshot and clear the queue before draining to avoid re-queuing on failure.
    const pending = _queue.splice(0, _queue.length);

    for (const { config, resolve, reject } of pending) {
      try {
        const response = await apiInstance(config);
        resolve(response);
      } catch (err) {
        reject(err);
      }
    }
  },

  size() {
    return _queue.length;
  },
};

export default offlineQueue;
